import httpUtils from '@/utils/http-utils';
import i18n from '@/plugins/vue-i18n';
import {
  getDefaultAvailability,
  getCurrentMarket,
  getBrowsingPortausCompanyId,
  getPortausCompanyOverride,
  MARKET_ON,
  PORTAUS_COMPANY,
} from '@/utils/market-utils';

const baseRequesterOptions = {
  baseUrl: `${window.processEnv.VUE_APP_CMS_API_BASE_URL_V1}/products`,
  // baseUrl: `${window.processEnv.VUE_APP_CMS_API_BASE_URL}/products`,
  requesterType: 'CMS_WEB_SERVICE',
};

const baseRequesterOptionsC = {
  baseUrl: `${window.processEnv.VUE_APP_CMS_API_BASE_URL}`,
  requesterType: 'CMS_WEB_SERVICE',
}

const baseRequesterOptionsA = {
  baseUrl: `${window.processEnv.VUE_APP_CMS_API_BASE_URL}/products`,
  requesterType: 'CMS_WEB_SERVICE',
};

const baseRequesterOptionsB = {
  baseUrl: `${window.processEnv.VUE_APP_CMS_API_BASE_URL_V1}/product-inventories`,
  // baseUrl: `${window.processEnv.VUE_APP_CMS_API_BASE_URL}/products`,
  requesterType: 'CMS_WEB_SERVICE',
};

const productApiReqNotification = httpUtils.createRequester(baseRequesterOptions);
const productApiReqErrorHandling = httpUtils.createRequester(baseRequesterOptions, 'errorHandling');

const productApiReqNotificationA = httpUtils.createRequester(baseRequesterOptionsA);
const productApiReqErrorHandlingA = httpUtils.createRequester(baseRequesterOptionsA, 'errorHandling');

const productApiReqNotificationB = httpUtils.createRequester(baseRequesterOptionsB);
const productApiReqErrorHandlingB = httpUtils.createRequester(baseRequesterOptionsB, 'errorHandling');

const productApiReqNotificationC = httpUtils.createRequester(baseRequesterOptionsC);
const productApiReqErrorHandlingC = httpUtils.createRequester(baseRequesterOptionsC, 'errorHandling');

// ---------------------------------------------------------------------------
// Market scoping — fail closed.
//
// The CMS search endpoints treat a missing `inventory` as "every inventory of
// every company", so a request that omits it answers with the same wine once
// per company, at each company's price — and those foreign-company variants
// could be added to the cart. That single fail-open default is what produced
// the Querce Seconda / I Forestieri duplicates ("des produits en double à deux
// prix différents") and Quebec products listing on the Ontario site.
//
// An empty array is dropped from the query string by axios, and `[]` is truthy
// in JS, so `availability ? availability : fallback` does NOT catch it — an
// empty selection has to be tested for explicitly.
//
// Every listing therefore goes through scopedInventory(): a caller that names
// its own inventories keeps them (including the boutique's STORE_* codes), and
// a caller that names none gets the visitor's market rather than everything.
// No listing request may leave this file unscoped.
// ---------------------------------------------------------------------------
const isEmptySelection = (value) => (
  Array.isArray(value)
    ? value.length === 0
    : value == null || value === ''
);

const marketInventory = () => getDefaultAvailability(localStorage.getItem('countryCode'));

// `fallback` lets a caller keep its own documented default (see downloadPDF)
// while still being protected against the empty-selection hole.
const scopedInventory = (availability, fallback) => {
  if (!isEmptySelection(availability)) {
    return availability;
  }

  const scope = !isEmptySelection(fallback) ? fallback : marketInventory();

  if (process.env.NODE_ENV !== 'production' && isEmptySelection(scope)) {
    // Only reachable if getDefaultAvailability() ever returns nothing for a
    // market — which would silently un-scope every listing on the site.
    // eslint-disable-next-line no-console
    console.error('[product-api] refusing to build an unscoped listing request');
  }

  return scope;
};

// Inventory codes are not unique across companies: "IP" exists for both
// Boires (inventory 1, company 1) and PlanVin (inventory 17, company 4), so
// scoping by inventory code alone let PlanVin's Château Picoron products into
// the QC listing. The listings therefore also send the Portaus company
// (1 = Boires, 2 = Boires Canada), which /product-inventories/search/ honours.
//
// The company follows the inventories actually requested, not the visitor's
// market: the home page and menu link every boutique from both markets, and
// the Toronto store asked for as company 1 comes back empty. A selection that
// mixes both markets sends no company, as before.
const ONTARIO_CODES = getDefaultAvailability(MARKET_ON).concat('STORE_TORONTO')
  .map(code => code.toUpperCase());

const companyForInventory = (inventory) => {
  const codes = (Array.isArray(inventory) ? inventory : [inventory])
    .filter(code => typeof code === 'string' && code !== '')
    .map(code => code.toUpperCase());

  if (codes.length === 0) {
    return undefined;
  }

  const ontario = codes.filter(code => ONTARIO_CODES.indexOf(code) !== -1).length;

  if (ontario === codes.length) {
    return PORTAUS_COMPANY.BOIRES_CANADA;
  }

  return ontario === 0 ? PORTAUS_COMPANY.BOIRES : undefined;
};

// The ?company= preview override wins, so the future PlanVin/Négoce sites can
// be previewed with their own products (see getBrowsingPortausCompanyId).
const listingCompanyId = (availability) => (
  getPortausCompanyOverride() || companyForInventory(scopedInventory(availability))
);

const getRequester = (errorManagementType) => {
  if (errorManagementType === 'errorHandling') {
    return productApiReqErrorHandling;
  }

  return productApiReqNotification;
};

// eslint-disable-next-line no-unused-vars
const getRequesterA = (errorManagementType) => {
  if (errorManagementType === 'errorHandling') {
    return productApiReqErrorHandlingA;
  }
  return productApiReqNotificationA;
};

const getRequesterB = (errorManagementType) => {
  if (errorManagementType === 'errorHandling') {
    return productApiReqErrorHandlingB;
  }
  return productApiReqNotificationB;
};

const getRequesterC = (errorManagementType) => {
  if (errorManagementType === 'errorHandling') {
    return productApiReqErrorHandlingC;
  }
  return productApiReqNotificationC;
};

class ProductApi {
  fetchProducerProduct(filters = {}, errorManagementType) {
    let taxInc = undefined;
    if (filters.maxPrice && filters.maxPrice != null && filters.tax) {
      taxInc = filters.tax == 'IN' ?
        {
          minPriceTaxIn: filters.minPrice != null ? filters.minPrice : undefined,
          maxPriceTaxIn: filters.maxPrice != null ? filters.maxPrice : undefined
        } :
        {
          minPrice: filters.minPrice != null ? filters.minPrice : undefined,
          maxPrice: filters.maxPrice != null ? filters.maxPrice : undefined
        };
    }
    return getRequester(errorManagementType)
      .get('/search/', {
        // .get('/elastic', {
        headers: {
          'accept-language': i18n.locale,
        },
        params: {
          page: filters.page,
          // size: filters.size,
          type: filters.productTypes,
          // /products/search/ does filter on `inventory` (an unmatched code
          // returns 0 documents); it just doesn't trim the productInventories
          // array of the documents it keeps, which is what the callers pick
          // the market variant from. Sending it keeps other companies' wines
          // out of the producer carousels.
          inventory: scopedInventory(filters.availability),
          a: filters.attributes,
          c: filters.countries,
          r: filters.regions,
          t: filters.text,
          sortByPrice: filters.sortByPrice,
          sortByQty: filters.sortByQty,
          show: filters.show,
          providerId:filters.providerId ? filters.providerId : undefined,
          sortByOrigin:"ASC",
          // This endpoint returns product SHEETS (fiches produits), which are
          // scoped by company — so the provider's carousel can be filtered.
          // The main products listing goes through
          // /product-inventories/search/, scoped the same way below.
          portausCompanyId: getBrowsingPortausCompanyId(getCurrentMarket()),
          ...taxInc
        },
      }).then(res => res && res.data);
  }
  fetchProductsBoutique(filters = {}, errorManagementType) {
    let taxInc = undefined;
    if (filters.maxPrice && filters.maxPrice != null && filters.tax) {
      taxInc = filters.tax == 'IN' ?
        {
          minPriceTaxIn: filters.minPrice != null ? filters.minPrice : undefined,
          maxPriceTaxIn: filters.maxPrice != null ? filters.maxPrice : undefined
        } :
        {
          minPrice: filters.minPrice != null ? filters.minPrice : undefined,
          maxPrice: filters.maxPrice != null ? filters.maxPrice : undefined
        };
    }

    return getRequesterB(errorManagementType)
      .get('/search/', {
        // .get('/elastic', {
        headers: {
          'accept-language': i18n.locale,
        },
        params: {
          page: filters.page,
          size: filters.size,
          type: filters.productTypes,
          // The boutique names its own store (STORE_MTL / STORE_QC /
          // STORE_TORONTO) and that selection is kept as-is. It is only the
          // callers that pass no selection at all — product.vue's
          // getProductLists() calls fetchProductsBoutique({}) — that used to
          // send an unscoped search and get every company's catalogue back.
          inventory: scopedInventory(filters.availability),
          a: filters.attributes,
          c: filters.countries,
          r: filters.regions,
          t: filters.text,
          // min: filters.minPrice,
          // max: filters.maxPrice,
          show: filters.show,
          sortByPrice: 'DESC',
          sortByQty: filters.sortByQty,
          providerId:filters.providerId ? filters.providerId : undefined,
          sortByOrigin:"ASC",
          portausCompanyId: listingCompanyId(filters.availability),
          ...taxInc
        },
      }).then(res => res && res.data);
  }

  fetchProducts(filters = {}, errorManagementType) {
    let taxInc = undefined;
    if (filters.maxPrice && filters.maxPrice != null && filters.tax) {
      taxInc = filters.tax == 'IN' ?
        {
          minPriceTaxIn: filters.minPrice != null ? filters.minPrice : undefined,
          maxPriceTaxIn: filters.maxPrice != null ? filters.maxPrice : undefined
        } :
        {
          minPrice: filters.minPrice != null ? filters.minPrice : undefined,
          maxPrice: filters.maxPrice != null ? filters.maxPrice : undefined
        };
    }
    return getRequesterB(errorManagementType)
      .get('/search/', {
        // .get('/elastic', {
        headers: {
          'accept-language': i18n.locale,
        },
        params: {
          page: filters.page,
          size: filters.size,
          type: filters.productTypes,
          inventory: scopedInventory(filters.availability),
          a: filters.attributes,
          c: filters.countries,
          r: filters.regions,
          t: filters.text,
          sortByPrice: filters.sortByPrice,
          sortByQty: filters.sortByQty,
          show: filters.show,
          providerId:filters.providerId ? filters.providerId : undefined,
          sortByOrigin:"ASC",
          portausCompanyId: listingCompanyId(filters.availability),
          ...taxInc
        },
      }).then(res => res && res.data);
  }
  downloadPDF(filters = {}, errorManagementType) {
    let taxInc = undefined;
    if (filters.maxPrice && filters.maxPrice != null && filters.tax) {
      taxInc = filters.tax == 'IN' ?
        {
          minPriceTaxIn: filters.minPrice != null ? filters.minPrice : undefined,
          maxPriceTaxIn: filters.maxPrice != null ? filters.maxPrice : undefined
        } :
        {
          minPrice: filters.minPrice != null ? filters.minPrice : undefined,
          maxPrice: filters.maxPrice != null ? filters.maxPrice : undefined
        };
    }
    return getRequesterB(errorManagementType)
    .get('/listing', {
      // .get('/elastic', {
      headers: {
        'accept-language': i18n.locale,
      },
      params: {
        page: filters.page,
        size: filters.size,
        type: filters.productTypes,
        // NOTE: the PDF price list deliberately omits SAQ from its Quebec
        // default — a board listing is not something Boires sells directly —
        // so it keeps its own fallback rather than getDefaultAvailability().
        // The `? :` it used before was the empty-selection hole: `[]` is
        // truthy, so an empty entrepôt selection sent an unscoped request and
        // the exported PDF listed every company's products at every company's
        // price.
        inventory: scopedInventory(
          filters.availability,
          localStorage.getItem("countryCode") === 'ON'
            ? ['LCBO', 'PI', 'MERCH_TORONTO']
            : ['IP', 'Brouhh', 'MERCH_MTL', 'BERGEVILLE'],
        ),
        a: filters.attributes,
        c: filters.countries,
        r: filters.regions,
        t: filters.text,
        sortByPrice: filters.sortByPrice,
        sortByQty: filters.sortByQty,
        show: filters.show || 'AVAILABLE_UPCOMING',
        providerId:filters.providerId ? filters.providerId : undefined,
        sortByOrigin:"ASC",
        showUvc: true,
        showVintage: true,
        showVolume: true,
        showQuantity: false,
        showPrices: true,
        taxes: filters.tax ? filters.tax : (localStorage.getItem("tax") === 'IN' ? 'IN' : 'OUT'),
        region: localStorage.getItem("countryCode") === 'QC' ? 'QUEBEC': 'ONTARIO',
        ...taxInc
      },
      responseType: 'blob',
    }).then((res) => {
      const data = res && res.data;

      if (typeof Blob !== 'undefined' && data instanceof Blob) {
        return window.URL.createObjectURL(data);
      }

      return data;
    });

  }

  // ElasticSearch-backed lookup (GET /api/v1/products/search/{id}?idType=id)
  // instead of the slow direct-DB GET /api/latest/products/{id}. A product can
  // have several puids (one per inventory variant); they are exposed as
  // productInventories[].puid in this response — there is no top-level puid.
  fetchCmsProduct(id, errorManagementType) {
    return getRequester(errorManagementType)
      .get(`/search/${id}`, {
        headers: {
          'accept-language': i18n.locale,
        },
        params: {
          idType: 'id',
        },
      }).then(res => res && res.data);
  }

  // Vanity URL support: resolve a product slug to an object carrying its puid.
  // Used when a clean URL carries only a slug and no ?id=.
  //
  // Fast path: the ES endpoints don't support idType=slug directly, but the
  // ES listing search accepts free text (t=) and its results include both slug
  // and puid — so search the slug's words and exact-match the slug field.
  // A product can match once per inventory variant; prefer the visitor's
  // market. Products without active inventory are absent from the ES listing,
  // so fall back to the slow direct-DB endpoint (GET /api/latest/products/
  // {slug}?idType=slug) only for those.
  resolveSlug(slug, errorManagementType) {
    return getRequesterB(errorManagementType)
      .get('/search/', {
        headers: {
          'accept-language': i18n.locale,
        },
        params: {
          t: slug.replace(/-/g, ' '),
          size: 50,
          // Scoped like every other listing: unscoped, a slug that exists in
          // both markets could resolve to the other market's variant and pin
          // the product page to it. Out-of-market products now fall through to
          // resolveSlugLegacy exactly as products with no active inventory do.
          inventory: marketInventory(),
        },
      })
      .then((res) => {
        const results = (res && res.data && res.data.results) || [];
        const matches = results.filter(r => r && r.slug === slug && r.puid);
        if (matches.length === 0) {
          return this.resolveSlugLegacy(slug, errorManagementType);
        }
        const market = localStorage.getItem('countryCode') || 'QC';
        const allowedCodes = getDefaultAvailability(market).map(c => c.toUpperCase());
        const inMarket = matches.find(r =>
          r.inventory && allowedCodes.indexOf(r.inventory.code.toUpperCase()) !== -1);
        return inMarket || matches[0];
      })
      .catch(() => this.resolveSlugLegacy(slug, errorManagementType));
  }

  resolveSlugLegacy(slug, errorManagementType) {
    return getRequesterA(errorManagementType)
      .get(`/${slug}`, {
        headers: {
          'accept-language': i18n.locale,
        },
        params: {
          idType: 'slug',
        },
      }).then(res => res && res.data);
  }

  // Scoped by construction: the caller names the exact inventory variants it
  // wants (puids are per variant), so there is no market default to apply.
  fetchProductsByPuid(filters = [], errorManagementType) {
    if (!Array.isArray(filters)) {
      filters = [filters];
  }
    const queryString = filters
            .map(puid => `puid=${encodeURIComponent(puid)}`)
            .join('&');
    return getRequesterB(errorManagementType)
      .get(`search/?${queryString}`, {
        headers: {
          'accept-language': i18n.locale,
        },
      }).then(res => res && res.data);
  }

  fetchProduct(id, idType, errorManagementType) {

    return getRequesterB(errorManagementType)
      .get(`/search/${id}`, {
        // .get(`/${id}`, {
        headers: {
          'accept-language': i18n.locale,
        },
        params: {
          idType,
        },
      }).then(res => res && res.data);
  }

  fetchProductInv(id, idType, errorManagementType) {

    return getRequester(errorManagementType)
      .get(`/search/${id}`, {
        // .get(`/${id}`, {
        headers: {
          'accept-language': i18n.locale,
        },
        params: {
          idType,
        },
      }).then(res => res && res.data);
  }

  fetchProductBoutique(id, idType, errorManagementType) {

    return getRequesterB(errorManagementType)
      .get(`/search/${id}`, {
        // .get(`/${id}`, {
        headers: {
          'accept-language': i18n.locale,
        },
        params: {
          idType,
        },
      }).then(res => res && res.data);
  }

  getSingleProduct(id, idType, errorManagementType) {
    return getRequester(errorManagementType)
      .get(`/search/${id}`, {
        headers: {
          'accept-language': i18n.locale,
        },
        params: {
          idType,
        },
      }).then(res => res && res.data);
  }

  fetchCarouselProducts(group, errorManagementType) {
    return getRequesterC(errorManagementType)
      .get('/carousels', {
        headers: {
          'accept-language': i18n.locale,
        },
        params: {
          group,
        },
      }).then(res => res && res.data);
  }
}

export default new ProductApi();
